/**
 * CORE-G-5 shared row-action primitives.
 *
 * Chans's 2026-09-15 rebuild spec §7 G-5:
 *   "One action model: Pause / Ignore / Snooze; negatives Yes / No;
 *    snooze 30 days."
 *
 * Two primitives ship together:
 *
 *   - <RecommendationActionRow> — Pause / Ignore / Snooze. The
 *     management surfaces (M-04 Campaign Review, M-05 Ad Group Review,
 *     M-06 Ad Review, M-07 Keyword Review, M-08 Budget Reallocation
 *     with the R-2 caveat) render one row per finding with these three
 *     verbs. Snooze is fixed at 30 days per spec; the button label
 *     names the duration so an operator knows what they are agreeing
 *     to.
 *
 *   - <NegativeYesNoRow> — Yes / No. Negative-keyword suggestions
 *     (M-03) and search-terms confirmations use a binary decision, so
 *     the button pair is Yes / No.
 *
 * ⛔ No ad-hoc verbs. Chans's spec bans "Approve", "Apply", "Assign"
 * as row-action labels — the noSharedRowActionVerbs test asserts none
 * of these leak into ads-scoped source. Budget Push Queue is the
 * documented R-2 exception; see the test for the allowlist rationale.
 *
 * Snooze at 30 days
 *
 *   The primitive owns the "30 days" copy so the same duration ships
 *   on every adopter. If R-2 later lets an operator pick a different
 *   duration, that landing changes this file's default and no other
 *   tab has to be swept.
 */
import { Ban, Bell, PauseCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Pause / Ignore / Snooze — the standard management-row verbs.
// ---------------------------------------------------------------------------

export interface RecommendationActionRowProps {
  /** Called when the operator confirms Pause on this row. */
  onPause: () => void;
  /** Called when the operator confirms Ignore (permanent no). */
  onIgnore: () => void;
  /** Called when the operator picks Snooze — always for the fixed
   *  30-day window Chans's spec sets. */
  onSnooze: () => void;
  /** Optional: disable all three buttons while the row's write is in flight. */
  pending?: boolean;
}

/**
 * Snooze duration, in days. Locked at 30 per spec §7 G-5. Consumers
 * that need the value (e.g. to write a `snoozed_until` timestamp)
 * should import this constant so the caller and the primitive stay
 * in agreement.
 */
export const SNOOZE_DAYS = 30;

export function RecommendationActionRow({
  onPause,
  onIgnore,
  onSnooze,
  pending = false,
}: RecommendationActionRowProps) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="destructive"
        onClick={onPause}
        disabled={pending}
      >
        <PauseCircle className="mr-1 h-3 w-3" aria-hidden="true" />
        Pause
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onIgnore}
        disabled={pending}
      >
        <Ban className="mr-1 h-3 w-3" aria-hidden="true" />
        Ignore
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onSnooze}
        disabled={pending}
        title={`Hide this row for ${SNOOZE_DAYS} days`}
      >
        <Bell className="mr-1 h-3 w-3" aria-hidden="true" />
        Snooze {SNOOZE_DAYS} days
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yes / No — negatives-suggestion variant.
// ---------------------------------------------------------------------------

export interface NegativeYesNoRowProps {
  /** Called when the operator confirms Yes (add negative). */
  onYes: () => void;
  /** Called when the operator confirms No (do not add). */
  onNo: () => void;
  pending?: boolean;
}

export function NegativeYesNoRow({
  onYes,
  onNo,
  pending = false,
}: NegativeYesNoRowProps) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="default"
        onClick={onYes}
        disabled={pending}
      >
        Yes
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onNo}
        disabled={pending}
      >
        No
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adoption register
// ---------------------------------------------------------------------------

/**
 * Sections that render row-level actions. `verbSet` names which of the
 * two variants a section uses. `adopted` flips to true once the section
 * imports the primitive; new adopters flip it in the same PR.
 *
 * Bus-factor: a reader who wants to know which section uses which
 * verbs opens this map, not the individual tab files.
 */
export interface RowActionAdoption {
  section: string;
  ticket: string;
  verbSet: 'pause-ignore-snooze' | 'yes-no';
  adopted: boolean;
  note?: string;
}

export const ROW_ACTION_ADOPTED_BY: readonly RowActionAdoption[] = [
  {
    section: 'Campaign Review',
    ticket: 'CORE-M-04',
    verbSet: 'pause-ignore-snooze',
    adopted: false,
    note: 'Shell today (Q-6). Adoption lands with the row list.',
  },
  {
    section: 'Ad Group Review',
    ticket: 'CORE-M-05',
    verbSet: 'pause-ignore-snooze',
    adopted: false,
    note: 'Shell today (Q-6). Adoption lands with the row list.',
  },
  {
    section: 'Ad Review',
    ticket: 'CORE-M-06.b',
    verbSet: 'pause-ignore-snooze',
    adopted: false,
    note: 'AdHealthTable has its own row shape; adoption lands with M-06.b.',
  },
  {
    section: 'Keyword Review',
    ticket: 'CORE-M-07',
    verbSet: 'pause-ignore-snooze',
    adopted: false,
    note: 'Shell today (Q-3). Adoption lands with the row list.',
  },
  {
    section: 'Budget Reallocation',
    ticket: 'CORE-M-08.b',
    verbSet: 'pause-ignore-snooze',
    adopted: false,
    note:
      'Interior BudgetReallocationTab has its own row shape; adoption lands with M-08.b. R-2 open on whether Apply/Snooze semantics differ for budget rows.',
  },
  {
    section: 'Negative Keyword Suggestions',
    ticket: 'CORE-M-03.c',
    verbSet: 'yes-no',
    adopted: true,
    note:
      'M-03 wrapper renders NegativeYesNoRow directly (CORE-M-03.c, 2026-09-18). The standalone /ads-tools/search-terms route still runs its own multi-select UI until phase D retires it.',
  },
];

export default RecommendationActionRow;
